// --- Our Modules --------------------------------------------------------------------------------
import constants from "@/config/constants";
import type { TyLang, TyLangValue } from "@/types";
// --- Logic --------------------------------------------------------------------------------------
export { pressToOpen, rightClickToOpen } from "./cards";
export { isValidSlug, sanitizeSlugInput } from "./slug";

/**
 * Reads a tenant-authored bilingual field. A company may publish in one language only, so the
 * asked-for entry is a preference and the first one that exists is the answer.
 */
export function readLangValue(values: TyLangValue[] | undefined, lang: TyLang): string {
    if (!values?.length) return "";
    return values.find((entry) => entry.lang === lang)?.value ?? values[0].value;
}

/**
 * Riyals as a person would say them: grouped, never rounded, decimals only when there are any.
 *
 * **Always Western digits, in both languages.** Arabic-Indic numerals are correct Arabic and wrong
 * here: these figures are read off bank transfers and typed back into them, and a number somebody
 * cannot copy straight across is a number they have to retype.
 */
export function formatMoney(value: number): string {
    return new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(value);
}

/** A plain count, grouped. Same digits rule as `formatMoney`, and for the same reason. */
export function formatNumber(value: number): string {
    return new Intl.NumberFormat("en-US").format(value);
}

/**
 * Megabytes as a person would say them — the API reports storage in MB, and "6.4 GB" is the number
 * somebody decides on where "6553" is not.
 */
export function formatStorage(megabytes: number, lang: TyLang = "ar"): string {
    const units = lang === "ar" ? ["م.ب", "ج.ب", "ت.ب"] : ["MB", "GB", "TB"];
    if (!megabytes) return `0 ${units[0]}`;

    let step = Math.floor(Math.log(megabytes) / Math.log(1024));
    step = Math.min(Math.max(step, 0), units.length - 1);
    const value = megabytes / 1024 ** step;

    return `${step >= 1 ? value.toFixed(1) : Math.round(value)} ${units[step]}`;
}

/** A date as a person reads it. The API sends ISO strings; nobody wants to read one. */
export function formatDate(value: string | null | undefined, lang: TyLang = "ar"): string {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return new Intl.DateTimeFormat(lang === "ar" ? "ar-SA-u-nu-latn-ca-gregory" : "en-GB", {
        year: "numeric",
        month: "short",
        day: "numeric",
    }).format(date);
}

/** A date **and** a time, for a transfer or a password reset — "yesterday" settles no dispute. */
export function formatDateTime(value: string | null | undefined, lang: TyLang = "ar"): string {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return new Intl.DateTimeFormat(lang === "ar" ? "ar-SA-u-nu-latn-ca-gregory" : "en-GB", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    }).format(date);
}

/**
 * Whole days from now until a date — negative once it is past.
 *
 * Rounded up, not down: a subscription that lapses in nine hours has one day left, and a dashboard
 * that calls that "0 days" has told an admin the thing already happened.
 */
export function daysUntil(value: string | null | undefined): number | null {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return Math.ceil((date.getTime() - Date.now()) / 86_400_000);
}

/** How full a meter is, as a percentage capped at 100 — a figure over its own limit still fits a bar. */
export function usedPercent(used: number, limit: number): number {
    if (!limit) return 0;
    return Math.min(100, Math.round((used / limit) * 100));
}

/**
 * The tone a storage meter is drawn in. Storage is the one number that can actually stop a company
 * working, so it changes colour before it runs out rather than after.
 */
export function storageTone(percent: number): "normal" | "warn" | "bad" {
    if (percent >= 95) return "bad";
    if (percent >= 80) return "warn";
    return "normal";
}

/**
 * Whether a nav link points at the section the person is already in. Such a link **replaces** rather
 * than pushes: tapping a nav button to get back to a section's top would otherwise stack an entry,
 * and the back button would walk them straight back in.
 */
export function isSectionReentry(path: string, pathname: string): boolean {
    return path === pathname;
}

/**
 * Where an organization's public catalog lives.
 *
 * Built from the API's own address rather than a second environment variable: they are the same
 * server, and two settings that must agree are one setting somebody will eventually get wrong.
 */
export const catalogUrl = (slug: string): string => `${String(constants.url).replace(/\/admins\/?$/, "")}/catalog/${encodeURIComponent(slug)}`;
